import { Box, Typography, Button } from "@mui/material";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import type { CheckinConfirmationProps } from "../../../types";
import {BrandedSelect, BrandedTextField} from "../../form";
import useOdooApi from "../../../hooks/useOdooApi";
import { useState } from "react";

const prefixThaiOptions = [
  { label: "นาย", value: "นาย" },
  { label: "นาง", value: "นาง" },
  { label: "นางสาว", value: "นางสาว" },
];

export default function CheckinConfirmation({
  checkinData,
  onConfirm,
  onBack,
}: CheckinConfirmationProps) {
  const { patientInfo, bookingId, photo } = checkinData;
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [extraForm, setExtraForm] = useState<Record<string, string>>({});
  const odooApi = useOdooApi();

  // Function to compress and convert image to base64
  const compressImage = async (
    imageDataUrl: string,
    quality: number = 0.8
  ): Promise<string> => {
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions (max width/height 800px)
        const maxDimension = 800;
        let { width, height } = img;

        if (width > height) {
          if (width > maxDimension) {
            height = (height * maxDimension) / width;
            width = maxDimension;
          }
        } else {
          if (height > maxDimension) {
            width = (width * maxDimension) / height;
            height = maxDimension;
          }
        }

        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx?.drawImage(img, 0, 0, width, height);
        const compressedDataUrl = canvas.toDataURL("image/jpeg", quality);
        resolve(compressedDataUrl);
      };

      img.src = imageDataUrl;
    });
  };

  const handleExtraFormChange = (value: string, field: string) => {
      setExtraForm({ ...extraForm, [field]: value });
  }

  const handleConfirm = async () => {
    if (!photo || !bookingId) {
      console.error("Missing photo or booking ID");
      return;
    }

    setLoading(true);

    try {
      // Compress image and convert to base64
      const compressedPhoto = await compressImage(photo, 0.7);

      // Remove data:image/jpeg;base64, prefix to get pure base64
      const base64Photo = compressedPhoto.replace(
        /^data:image\/[a-z]+;base64,/,
        ""
      );

      // Call saveCheckIn API
      const response = await odooApi.saveCheckIn({
        booking_id: bookingId,
        image_personal: base64Photo,
        title_th: extraForm.title_th ?? undefined,
        first_name_th: extraForm.first_name_th ?? undefined,
        last_name_th: extraForm.last_name_th ?? undefined,
      });

      setLoading(false);

      if (response.code !== "success") {
        alert("เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ");
        return;
      }

      onConfirm();
    } catch (error) {
      console.error("Save check-in failed:", error);
      setLoading(false);
      // You might want to show an error message to the user here
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ maxWidth: 600, mx: "auto" }}>
        <Typography
          variant="h4"
          sx={{ color: "#7479AE", fontWeight: 700, mb: 0, textAlign: "center" }}
        >
          {t("admin.checkin.title")}
        </Typography>

        {/* Barcode Display */}
        <Box sx={{ textAlign: "center", p: 3, borderRadius: 2 }}>
          <Box
            sx={{
              display: "inline-block",
            }}
          >
            {/* Show actual barcode image if available */}
            {photo ? (
              <Box sx={{ mb: 1 }}>
                <img
                  src={photo}
                  alt="Barcode"
                  style={{
                    height: 200,
                    objectFit: "contain",
                  }}
                />
              </Box>
            ) : (
              /* Fallback barcode representation */
              <Box
                sx={{
                  background:
                    "repeating-linear-gradient(90deg, #000 0px, #000 1px, #fff 1px, #fff 2px, #000 2px, #000 3px, #fff 3px, #fff 5px)",
                  height: 200,
                  width: 250,
                  mb: 1,
                }}
              />
            )}
          </Box>
        </Box>

        {/* Patient Information */}
        {patientInfo && (
          <Box sx={{ mb: 3, px: 1 }}>
            {!patientInfo.is_question && <Button
              component="a"
              // href={`/questionnaire?order_id=${patientInfo.booking_id}`}
                 onClick={() => {
                window.open(
                  `/questionnaire?back=1&order_id=${patientInfo.booking_id}`,
                  '_blank',
                );
              }}
              target="_blank"
              sx={{
                marginTop: "16px",
                width: "100%",
                fontSize: "1.4rem",
                color: "white",
                background: "#7479ae",
                backgroundSize: "200% 200%",
                backgroundPosition: "left center",
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: {background: "#5f6390"},
              }}
            >
              ทำแบบสอบถาม
            </Button>}

            {!patientInfo.is_pdpa_sign && <Button
              component="a"
              onClick={() => {
                window.open(
                  `/sign-agree?order_id=${patientInfo.booking_id}`,
                  '_blank'
                );
              }}
              target="_blank"
              sx={{
                marginTop: "16px",
                width: "100%",
                fontSize: "1.4rem",
                color: "white",
                background: "#7479ae",
                backgroundSize: "200% 200%",
                backgroundPosition: "left center",
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: {background: "#5f6390"},
              }}
            >
              เซ็นยินยอมเข้ารับบริการ
            </Button>}

            <BrandedSelect
              label={t("admin.checkin.patientInfo.title_name")}
              value={patientInfo.title_th}
              onChange={(e) =>
                handleExtraFormChange(e.target.value as string, "title_th")
              }
              options={prefixThaiOptions}
            />
            <BrandedTextField
              label={t("admin.checkin.patientInfo.name")}
              value={patientInfo.first_name_th}
              onChange={(e) => handleExtraFormChange(e.target.value, "first_name_th")}
              InputProps={{
                  sx: { color: "#6e6e6f !important" },
              }}
            />
            <BrandedTextField
              label={t("admin.checkin.patientInfo.last_name")}
              value={patientInfo.last_name_th}
              onChange={(e) => handleExtraFormChange(e.target.value, "last_name_th")}
              InputProps={{
                  sx: { color: "#6e6e6f !important" },
              }}
            />
            <BrandedTextField
              label={t("admin.checkin.patientInfo.full_name_eng")}
              value={patientInfo.name}
              InputProps={{
                readOnly: true,
                sx: { color: "#6e6e6f !important" },
              }}
            />

            <BrandedTextField
              label={t("admin.checkin.patientInfo.nationalId")}
              value={
                patientInfo.id
                  ? patientInfo.id
                      .replace(/\D/g, "")
                      .slice(0, 13)
                      .replace(
                        /^(\d{0,1})(\d{0,4})(\d{0,5})(\d{0,2})(\d{0,1})$/,
                        (_match, p1, p2, p3, p4, p5) =>
                          [p1, p2, p3, p4, p5].filter(Boolean).join("-")
                      )
                  : ""
              }
              InputProps={{
                readOnly: true,
                sx: { color: "#6e6e6f !important" },
              }}
            />

            <Box display="flex" gap={2}>
              <Box sx={{ flex: 1 }}>
                <BrandedTextField
                  label={t("admin.checkin.patientInfo.dateOfBirth")}
                  value="02/10/1976"
                  InputProps={{
                    readOnly: true,
                    sx: { color: "#6e6e6f !important" },
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <BrandedTextField
                  label={t("admin.checkin.patientInfo.age")}
                  value={patientInfo.age ? `${patientInfo.age} ปี` : ""}
                  InputProps={{
                    readOnly: true,
                    sx: { color: "#6e6e6f !important" },
                  }}
                />
              </Box>
            </Box>

            <BrandedTextField
              label={t("admin.checkin.patientInfo.phone")}
              value={
                patientInfo.phone
                  ? patientInfo.phone
                      .replace(/\D/g, "")
                      .slice(0, 10)
                      .replace(
                        /^(\d{0,3})(\d{0,3})(\d{0,4})$/,
                        (_match, p1, p2, p3) =>
                          [p1, p2, p3].filter(Boolean).join("-")
                      )
                  : ""
              }
              InputProps={{
                readOnly: true,
                sx: { color: "#6e6e6f !important" },
              }}
            />

            <BrandedTextField
              label={t("admin.checkin.patientInfo.email")}
              value={patientInfo.email}
              InputProps={{
                readOnly: true,
                sx: { color: "#6e6e6f !important" },
              }}
            />

            <BrandedTextField
              label={t("admin.checkin.patientInfo.appointmentDate")}
              value={
                patientInfo.appointmentDate
                  ? (() => {
                      // Format from "2025-08-15 06:55:59" to "15/08/2025 06:55"
                      const dateStr = patientInfo.appointmentDate;
                      if (dateStr.includes("-") && dateStr.includes(":")) {
                        const [datePart, timePart] = dateStr.split(" ");
                        const [year, month, day] = datePart.split("-");
                        const [hour, minute] = timePart.split(":");
                        return `${day}/${month}/${year} ${hour}:${minute}`;
                      }
                      return dateStr;
                    })()
                  : ""
              }
              InputProps={{
                readOnly: true,
                sx: { color: "#6e6e6f !important" },
              }}
            />

            <BrandedTextField
              label={t("admin.checkin.patientInfo.location")}
              value={patientInfo.location}
              InputProps={{
                readOnly: true,
                sx: { color: "#6e6e6f !important" },
              }}
            />
          </Box>
        )}

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "space-between",
            px: 1,
          }}
        >
          <Button
            variant="outlined"
            onClick={onBack}
            startIcon={<ArrowBackIcon />}
            sx={{
              color: "#7479AE",
              borderColor: "#7479AE",
              borderRadius: 3,
              py: 1.5,
              px: 3,
              "&:hover": {
                borderColor: "#5a5f8a",
                bgcolor: "#f5f5f5",
              },
            }}
          >
            {t("admin.checkin.back")}
          </Button>

          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={loading}
            sx={{
              bgcolor: "#7479AE",
              color: "white",
              borderRadius: 3,
              py: 2,
              px: 6,
              fontSize: 16,
              fontWeight: 600,
              "&:hover": {
                bgcolor: "#5a5f8a",
              },
              "&:disabled": {
                bgcolor: "#cccccc",
              },
            }}
            startIcon={<CheckCircleIcon />}
          >
            {loading
              ? t("common.saving") || "Saving..."
              : t("admin.checkin.confirmButton")}
          </Button>
        </Box>
      </Box>
    </motion.div>
  );
}
